'use client';

import { useCallback, useRef, useState } from 'react';
import type { MonthKey } from './types';

const SCROLL_END_TIMEOUT_MS = 50;

function listenForScrollEnd(callback: () => void): () => void {
  let timer: ReturnType<typeof setTimeout> | null = null;
  const onScroll = () => {
    if (timer) clearTimeout(timer);
    timer = setTimeout(() => {
      window.removeEventListener('scroll', onScroll);
      callback();
    }, SCROLL_END_TIMEOUT_MS);
  };
  window.addEventListener('scroll', onScroll, { passive: true });
  return () => {
    window.removeEventListener('scroll', onScroll);
    if (timer) clearTimeout(timer);
  };
}

function useScrollLock() {
  const scrollLockRef = useRef<string | null>(null);
  const [scrollLockVersion, setScrollLockVersion] = useState(0);

  const removeScrollListenerRef = useRef<(() => void) | null>(null);
  const fallbackTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const _releaseScrollLock = useCallback(() => {
    scrollLockRef.current = null;
    setScrollLockVersion(version => version + 1);
    if (fallbackTimerRef.current) {
      clearTimeout(fallbackTimerRef.current);
      fallbackTimerRef.current = null;
    }
  }, []);

  const acquireScrollLock = useCallback(
    (key: MonthKey) => {
      removeScrollListenerRef.current?.();
      _releaseScrollLock();
      scrollLockRef.current = key;

      removeScrollListenerRef.current = listenForScrollEnd(() => {
        removeScrollListenerRef.current = null;
        _releaseScrollLock();
      });

      fallbackTimerRef.current = setTimeout(() => {
        removeScrollListenerRef.current?.();
        removeScrollListenerRef.current = null;
        _releaseScrollLock();
      }, 2000);
    },
    [_releaseScrollLock]
  );

  const isScrollLocked = useCallback(() => scrollLockRef.current !== null, []);

  return { scrollLockVersion, acquireScrollLock, isScrollLocked };
}

export { useScrollLock };
